import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Subscription, SubscriptionResponse } from '../interfaces/subscription.interface';
import { ClassItem } from '../interfaces/class.interface';
import { ApiService } from './api.service';

export interface SubscriptionCheckResponse {
  canBook: boolean;
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {
  private readonly SUBSCRIPTION_STORAGE_KEY = 'user_subscriptions';
  private readonly SUBSCRIPTION_TIMESTAMP_KEY = 'subscription_fetch_timestamp';
  private readonly STALE_THRESHOLD = 1000 * 60 * 60; // 1 hour

  constructor(
    private http: HttpClient,
    private apiService: ApiService
  ) {}

  /**
   * Fetches the user's subscriptions from the API and caches them in localStorage.
   */
  fetchSubscriptions(): Observable<Subscription[]> {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      throw new Error('No auth token available');
    }

    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    return this.http.get<SubscriptionResponse>(
      `${this.apiService.getBaseApiUrl()}/clients/subscriptions`,
      { headers }
    ).pipe(
      map(response => {
        this.storeSubscriptions(response.items);
        return response.items;
      })
    );
  }

  /**
   * Stores subscriptions + timestamp in localStorage
   */
  private storeSubscriptions(subscriptions: Subscription[]): void {
    localStorage.setItem(this.SUBSCRIPTION_STORAGE_KEY, JSON.stringify(subscriptions));
    localStorage.setItem(this.SUBSCRIPTION_TIMESTAMP_KEY, Date.now().toString());
  }

  /**
   * Retrieves cached subscriptions if not stale. Otherwise returns null.
   */
  getStoredSubscriptions(): Subscription[] | null {
    const timestamp = localStorage.getItem(this.SUBSCRIPTION_TIMESTAMP_KEY);
    const subscriptions = localStorage.getItem(this.SUBSCRIPTION_STORAGE_KEY);

    if (!timestamp || !subscriptions) {
      return null;
    }

    const isStale = (Date.now() - parseInt(timestamp, 10)) > this.STALE_THRESHOLD;
    if (isStale) {
      return null;
    }

    return JSON.parse(subscriptions) as Subscription[];
  }

  /**
   * Main method to check if the user can join a class based on its behaviour & service category.
   */
  canJoinClass(
    classStartDateTime: string,
    classBehaviour: string,
    serviceCategoryId?: number | number[]
  ): boolean {
    // Anyone_Free → Always true
    if (classBehaviour.toLowerCase() === 'anyone_free') {
      return true;
    }

    // Otherwise, need to check subscriptions in storage
    const subscriptions = this.getStoredSubscriptions();
    if (!subscriptions) {
      return false;
    }

    // Convert to Date once for all checks
    const classDate = new Date(classStartDateTime);

    switch (classBehaviour.toLowerCase()) {
      case 'session_pack':
        // Need a valid subscription with ClassPackage matching the category
        return this.hasValidSessionPack(subscriptions, classDate, serviceCategoryId);

      case 'members_only_session':
        // Need a valid subscription with MemberShip
        return this.hasValidMembership(subscriptions, classDate);

      case 'membership_session_pack':
        // Must have (1) valid membership AND (2) valid session pack
        // They can be in different subscriptions.
        const hasMembership = this.hasValidMembership(subscriptions, classDate);
        const hasSessionPack = this.hasValidSessionPack(subscriptions, classDate, serviceCategoryId);
        return hasMembership && hasSessionPack;

      default:
        return false;
    }
  }

  /**
   * Check if there is at least one subscription that:
   *   - Is within the class date range
   *   - status = "Active" or "Future"
   *   - Has a ClassPackage with the matching serviceCategoryId
   */
  private hasValidSessionPack(
    subscriptions: Subscription[],
    classDate: Date,
    serviceCategoryId?: number | number[]
  ): boolean {
    return subscriptions.some(sub => {
      if (!this.isSubscriptionDateValid(sub, classDate)) {
        return false;
      }

      // Look for ClassPackage in either non-recurring or recurring arrays
      const hasClassPackageNonRecurring = sub.non_recurring_template_recurring_services.some(svc => {
        if (svc.service_behaviour !== 'ClassPackage') {
          return false;
        }
        return this.doesCategoryMatch(svc.service_category_id, serviceCategoryId);
      });

      const hasClassPackageRecurring = sub.recurring_template_recurring_services.some(svc => {
        if (svc.service_behaviour !== 'ClassPackage') {
          return false;
        }
        return this.doesCategoryMatch(svc.service_category_id, serviceCategoryId);
      });

      return hasClassPackageNonRecurring || hasClassPackageRecurring;
    });
  }

  /**
   * Check if there is at least one subscription that:
   *   - Is within the class date range
   *   - status = "Active" or "Future"
   *   - Has a MemberShip
   */
  private hasValidMembership(subscriptions: Subscription[], classDate: Date): boolean {
    return subscriptions.some(sub => {
      if (!this.isSubscriptionDateValid(sub, classDate)) {
        return false;
      }

      // Check for MemberShip in non-recurring or recurring
      const hasMembershipNonRecurring = sub.non_recurring_template_recurring_services
        .some(svc => svc.service_behaviour === 'MemberShip');

      const hasMembershipRecurring = sub.recurring_template_recurring_services
        .some(svc => svc.service_behaviour === 'MemberShip');

      return hasMembershipNonRecurring || hasMembershipRecurring;
    });
  }

  /**
   * Helper to ensure the subscription’s date range & status are valid for the class date.
   * status must be "Active" or "Future"
   */
  private isSubscriptionDateValid(subscription: Subscription, classDate: Date): boolean {
    if (!subscription.status || !['Active', 'Future'].includes(subscription.status)) {
      return false;
    }
    const start = new Date(subscription.start_date);
    const end = new Date(subscription.end_date);
    return classDate >= start && classDate <= end;
  }

  /**
   * Checks if the service’s category matches the class’s serviceCategoryId (single or array).
   * If class has service_category_id = [0], that typically means no category restriction.
   * Adjust if your business logic differs for 0.
   */
  private doesCategoryMatch(serviceCategoryOfSubscription: number, serviceCategoryId?: number | number[]): boolean {
    // If the class has serviceCategory 0 or undefined, interpret as no category needed
    if (!serviceCategoryId || (Array.isArray(serviceCategoryId) && serviceCategoryId.length === 0)) {
      return true;
    }

    if (Array.isArray(serviceCategoryId)) {
      // Return true if the subscription’s category matches ANY in the array
      return serviceCategoryId.includes(serviceCategoryOfSubscription);
    } else {
      return serviceCategoryOfSubscription === serviceCategoryId;
    }
  }

  /**
   * Public method returning an Observable with a canBook flag + optional message.
   */
  checkSubscriptionStatus(classItem: ClassItem): Observable<SubscriptionCheckResponse> {
    const canBook = this.canJoinClass(
      classItem.start_datetime,
      classItem.behaviour,
      // If classItem.service_category_id is an array, pass it as-is
      Array.isArray(classItem.service_category_id)
        ? classItem.service_category_id
        : undefined
    );

    return of({
      canBook,
      message: canBook ? undefined : this.getSubscriptionErrorMessage(classItem.behaviour)
    });
  }

  /**
   * Returns a user-facing message if a subscription requirement fails.
   */
  private getSubscriptionErrorMessage(behaviour: string): string {
    switch (behaviour.toLowerCase()) {
      case 'session_pack':
        return 'You need an active Session Pack (with matching category) to join this class.';
      case 'membership_session_pack':
        return 'You need both an active Membership and a matching Session Pack to join this class.';
      case 'members_only_session':
        return 'You need an active Membership to join this class.';
      default:
        return 'You do not have the required subscription to join this class.';
    }
  }
}
